import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaShareAlt, FaWhatsapp, FaPhone, FaRegCommentDots, FaMicrophone, FaDownload, FaTasks } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { API_BASE, apiFetch } from '../lib/api';
import * as Utils from "../utils";
import { FaCircleUp, FaPaperPlane } from 'react-icons/fa6';
import MarkdownMessage from './MarkdownMessage';

export type Message = {
    createdAt: string // "2026-01-30T18:28:39.815Z"
    id: string
    sender: 'user' | 'llm' | 'system'
    text: string
    mode: 'whatsapp' | 'callvoice' | 'webchat' | 'webvoice' | 'system',
    agent: string | null
}

const ModeIcon = ({ mode }: { mode: string }) => {
    switch (mode) {
        case 'whatsapp':
            return <FaWhatsapp className="mr-2 text-green-500" />;
        case 'callvoice':
            return <FaPhone className="mr-2 rotate-90 text-orange-500" />;
        case 'webvoice':
            return <FaMicrophone className="mr-2 text-purple-500" />;
        case 'webchat':
        default:
            return <FaRegCommentDots className="mr-2 text-blue-500" />;
    }
};

export default function Chat({
    messages,
    loading,
    onSend,
    communicationMode,
    distributor,
    waConvoLimit,
    sendWATemplate,
    handleFetchConcerns
}: {
    messages: Message[]
    loading: boolean,
    communicationMode: 'chat' | 'whatsapp'
    onSend: (text: string) => Promise<void>
    distributor: string
    waConvoLimit: moment.Moment,
    sendWATemplate: () => Promise<void>,
    handleFetchConcerns: () => Promise<void>
}) {

    // console.log("messages", messages);
    const [input, setInput] = useState('')
    // const [loading, setLoading] = useState(false)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const endRef = useRef<HTMLDivElement | null>(null)

    const whatsappSendAllowed = useMemo(() => {
        return moment().isBefore(waConvoLimit)
    }, [waConvoLimit])

    useEffect(() => {
        // scroll container to bottom when messages change
        const el = containerRef.current
        if (el) {
            // ensure DOM updated
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight
            })
        } else if (endRef.current) {
            endRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    const submit = async () => {
        const trimmed = input.trim()
        if (!trimmed) return
        setInput('')
        // setLoading(true)
        try {
            await onSend(trimmed)
        } finally {
            // setLoading(false)
        }
    }

    const handleDownloadChat = async () => {
        try {
            const response = await apiFetch(`${API_BASE}/api/download_dealer_messages_csv?distributor=${encodeURIComponent(distributor)}`)
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `dealer_messages_${distributor.replace(/[^a-z0-9_-]+/gi, '_')}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Downloaded")
        } catch {
            toast.error("Please, try again later")
        }
    }

    const handleShare = async () => {
        if (!distributor) return;
        const url = `${window.location.origin}/chat?distributor=${encodeURIComponent(distributor)}`;

        const shareData = {
            title: "Berger AI",
            text: `Chat with dealer: ${distributor}`,
            url: url,
        };

        await navigator.share(shareData);

        // navigator.clipboard.writeText(url).then(() => {
        //     setCopied(true);
        //     setTimeout(() => setCopied(false), 2000);
        // });
    };



    return (
        <div className="flex flex-col flex-grow bg-white rounded-lg shadow-md p-4">
            <div className="border-b pb-4 mb-4 flex justify-between items-center">
                <div>
                    <h1 className="text-xl font-bold">{Utils.formattedName(distributor)}</h1>
                    <div className='flex flex-row items-center gap-2 mt-1'>
                        <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${communicationMode === 'whatsapp' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {communicationMode === 'whatsapp' ? <FaWhatsapp /> : <FaRegCommentDots />}
                            <span>{communicationMode === 'chat' ? 'AI Chat' : 'WhatsApp'}</span>
                        </div>
                        {communicationMode === 'whatsapp' && (
                            <span className='text-xs text-gray-500'>
                                {whatsappSendAllowed ? `Available until ${moment(waConvoLimit).format('lll')}` : "24h window closed"}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    {/* TODO: Restore the call icon here when the new call workflow is implemented. */}
                    <button
                        onClick={handleFetchConcerns}
                        disabled={!distributor}
                        className="px-2 py-1 border border-transparent text-sm cursor-pointer font-medium rounded-md text-orange-700 bg-gray-200 hover:bg-orange-300 focus:outline-none disabled:bg-orange-100 disabled:cursor-not-allowed"
                        data-tooltip-id="my-tooltip"
                        data-tooltip-content="Show dealer response"
                    >
                        <FaTasks className="text-sm" />
                    </button>
                    <button
                        onClick={handleDownloadChat}
                        disabled={!distributor}
                        className="px-2 py-1 border border-transparent text-sm cursor-pointer font-medium rounded-md text-blue-700 bg-gray-200 hover:bg-blue-300 focus:outline-none disabled:bg-blue-100 disabled:cursor-not-allowed"
                        data-tooltip-id="my-tooltip"
                        data-tooltip-content="Download chat"
                    >
                        <FaDownload className="text-sm" />
                    </button>

                    <button
                        onClick={handleShare}
                        disabled={!distributor}
                        className="px-2 py-1 border border-transparent text-sm cursor-pointer font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                        data-tooltip-id="my-tooltip"
                        data-tooltip-content="Share Chat"
                    >
                        <FaShareAlt className="text-sm" />
                    </button>
                </div>
            </div>
            <div ref={containerRef} className="flex-grow h-0 overflow-y-auto mb-4 space-y-4 p-2 min-h-96">
                {messages.map((m) => {
                    if (m.sender === 'system') {
                        return (
                            <div key={m.id} className="flex justify-center">
                                <div className="text-sm text-gray-500 italic">
                                    {m.text}
                                </div>
                            </div>
                        )
                    }
                    return (
                        <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-lg px-4 py-2 rounded-2xl shadow ${m.sender === 'user' ? 'whitespace-pre-wrap bg-slate-600 text-white' : 'bg-gray-200 text-gray-800 border border-slate-300'}`}>
                                <div className="flex items-center">
                                    <ModeIcon mode={m.mode} />
                                    <p className="font-bold text-sm mb-1">{m.sender === 'user' ? 'You' : 'Berger AI'}</p>
                                </div>
                                {m.sender === 'user'
                                    ? <p>{m.text}</p>
                                    : <MarkdownMessage text={m.text} className="text-gray-800" />}
                                <p className={`text-xs mt-1 ${m.sender === 'user' ? 'text-blue-200' : 'text-gray-500'}`}>{moment(m.createdAt).format('lll')}</p>
                                {m.sender === 'user' && m.agent && <p className='text-xs mt-1 capitalize text-gray-300'>Agent: {m.agent}</p>}
                            </div>
                        </div>
                    )
                })}
                {loading && (
                    <div className="flex justify-start">
                        <div className="text-sm text-gray-500 italic animate-pulse">Berger AI is thinking...</div>
                    </div>
                )}
                <div ref={endRef} />
            </div>


            <div className="flex gap-2 border-t pt-4">
                {communicationMode === 'whatsapp' && !whatsappSendAllowed ? (
                    <button
                        onClick={sendWATemplate}
                        disabled={!distributor}
                        className="w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md
             text-white bg-lime-600 hover:bg-lime-700
             focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500
             disabled:bg-emerald-300 disabled:cursor-not-allowed cursor-pointer
             flex items-center justify-center gap-2"
                    >
                        <FaWhatsapp className="text-base" />
                        <span>WA Send Template</span>
                    </button>
                ) : (
                    <>
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
                            placeholder={distributor ? `Message as ${Utils.formattedName(distributor)}...` : 'Select an account to start'}
                            className="flex-1 border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading || !distributor}
                        />

                        <button
                            onClick={submit}
                            data-tooltip-id="my-tooltip"
                            data-tooltip-content={communicationMode === 'whatsapp' ? "Whatsapp Dealer" : "Ask AI"}
                            className={"px-6 py-2 text-white rounded-lg cursor-pointer disabled:cursor-not-allowed " + (communicationMode === 'chat' ? ' bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300' : ' bg-green-600 hover:bg-green-700 disabled:bg-green-300')}
                            disabled={loading || !input.trim()}
                        >
                            {communicationMode === 'whatsapp' ? <FaPaperPlane /> : <FaCircleUp />}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
